import { useCallback } from 'react';
import {
  ExternalMediaMedialink,
  ExternalMediaMedialinkWeblink,
  isExternalMediaGeoGebra,
  isExternalMediaH5P,
  isExternalMediaWeblink,
  isExternalMediaYoutube,
} from '../../../../../api/medialink';
import WebLinkTargets from '../../../../../constants/WeblinkTargets';
import { Node } from '../../../../../api/table-of-content';
import parseYoutubeURL from '../../../../../utils/parseYoutubeURL';
import EmbeddableMediaModal from '../../components/modals/EmbeddableMediaModal';
import { MedialinkDialogHeaderTags } from '../../../components/MedialinkDialogHeaderTags';
import { Module } from '../../../../../api/module';
import GeogebraModal from '../../components/modals/GeogebraModal';
import WeblinkModal from '../../components/modals/WeblinkModal';
import H5PModal from '../../components/modals/H5pModal';

function openWeblink(medialink: ExternalMediaMedialinkWeblink, node: Node, module: Module) {
  const url = medialink.externalMedia.href;

  switch (medialink.externalMedia.target) {
    case WebLinkTargets.MODAL:
      const parsedYoutubeURL = parseYoutubeURL(url);

      if (parsedYoutubeURL) {
        return (
          <EmbeddableMediaModal
            id="embeddable-media-modal"
            name={medialink.name}
            url={parsedYoutubeURL}
            headerProps={{
              title: medialink.name,
              tags: <MedialinkDialogHeaderTags material={medialink} module={module} />,
              titleTooltip: medialink.name,
            }}
          />
        );
      }

      return (
        <WeblinkModal
          url={url}
          id="weblink-modal"
          name={medialink.name}
          headerProps={{
            title: medialink.name,
            titleTooltip: medialink.name,
            tags: <MedialinkDialogHeaderTags material={medialink} module={module} />,
          }}
          weblinkInfo={medialink.externalMedia}
        />
      );

    case WebLinkTargets.SAME_WINDOW:
      window.location.assign(url);
      break;

    case WebLinkTargets.NEW_WINDOW: //handled by anchor tag
  }
}

export default function useExternalMediaMedialink() {
  const openExternalMediaMedialink = useCallback((medialink: ExternalMediaMedialink, node: Node, module: Module) => {
    if (isExternalMediaWeblink(medialink)) {
      return openWeblink(medialink, node, module);
    }

    if (isExternalMediaGeoGebra(medialink)) {
      return (
        <GeogebraModal
          id="geo-gebra-modal"
          medialink={medialink}
          headerProps={{
            title: medialink.name,
            tags: <MedialinkDialogHeaderTags material={medialink} module={module} />,
            titleTooltip: medialink.name,
          }}
        />
      );
    }

    if (isExternalMediaH5P(medialink)) {
      return (
        <H5PModal
          id="h5p-modal"
          medialink={medialink}
          headerProps={{
            title: medialink.name,
            tags: <MedialinkDialogHeaderTags material={medialink} module={module} />,
            titleTooltip: medialink.name,
          }}
        />
      );
    }

    if (isExternalMediaYoutube(medialink)) {
      let parsedYoutubeURL = parseYoutubeURL(`https://www.youtube.com/embed/${medialink.externalMedia.embedId}`);

      if (parsedYoutubeURL) {
        // parsedYoutubeURL already contains a searchParameter so we can add the other parameters with '&'
        if (medialink.externalMedia.start) parsedYoutubeURL += `&start=${medialink.externalMedia.start}`;
        if (medialink.externalMedia.end) parsedYoutubeURL += `&end=${medialink.externalMedia.end}`;

        return (
          <EmbeddableMediaModal
            id="embeddable-media-modal"
            name={medialink.name}
            url={parsedYoutubeURL}
            headerProps={{
              title: medialink.name,
              titleTooltip: medialink.name,
              tags: <MedialinkDialogHeaderTags material={medialink} module={module} />,
            }}
          />
        );
      }
    }
  }, []);

  return { openExternalMediaMedialink };
}
